import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { AmplitudeUserProperties } from "@lifeat/shared/amplitude/AmplitudeUserProperties";
import { Routes } from "@lifeat/shared/system/RouteConstants";
import { hasFreeAppAccess } from "@lifeat/shared/user/UserUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import GA4Event from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import { useUserContext } from "@contexts/UserContext";
import { sendClarityEvent } from "@features/clarity/Clarity";
import { ClarityEvent } from "@features/clarity/ClarityEvents";
import { useEvent } from "@hooks/useEvent";
import { logError } from "@service/ConsoleLoggerService";
import { callMonolith } from "@service/ServerlessFunctionsService";
import { buildRoute } from "@util/RouteUtil";
import { CompletedOnboardingStepEvent } from "../../features/onboarding/AmplitudeOnboardingEvents";
import { isUserFromAd } from "../../features/onboarding/OnboardingUtil";
import { OnboardingPersonalization } from "../../features/onboarding/components/OnboardingPersonalization";
import { OnboardingUpgradeWithTrial } from "../../features/onboarding/components/OnboardingUpgradeWithTrial";
import { OnboardingUpgradeWithoutTrial } from "../../features/onboarding/components/OnboardingUpgradeWithoutTrial";
import LoadingView from "../../features/virtual-space/components/SpaceView/LoadingView/LoadingView";

function OnboardingPage() {
    const history = useHistory();
    const amplitudeService = AmplitudeService.getInstance();
    const { userData, loading: userLoading } = useUserContext();

    const isAppBlocked = isUserFromAd() || !hasFreeAppAccess(userData);
    const [loading, setLoading] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    const openNextStep = () => {
        amplitudeService.logEvent(new CompletedOnboardingStepEvent({ isAppBlocked, stepIndex }));

        if (stepIndex < flow.length - 1) {
            setStepIndex((current) => current + 1);
        } else {
            history.push(buildRoute(Routes.App));
        }
    };

    const callOnboardingSurveyCompleteBackend = async () => {
        try {
            setLoading(true);
            await callMonolith("on-onboarding-survey-complete", undefined);
        } catch (e) {
            logError(e);
        } finally {
            setLoading(false);
        }
    };

    const flow = [
        <OnboardingPersonalization
            onNext={async () => {
                await callOnboardingSurveyCompleteBackend();
            }}
        />,
        <OnboardingUpgradeWithoutTrial onNext={openNextStep} />,
        <OnboardingUpgradeWithTrial onNext={openNextStep} />,
    ];
    const stepContent = flow[stepIndex];

    const onMount = useEvent(() => {
        GA4Service.logEvent(GA4Event.START_ONBOARDING);
        sendClarityEvent(ClarityEvent.START_ONBOARDING);
    });

    const onSurveyStatusChange = useEvent((requiresOnboardingSurvey: boolean | null | undefined) => {
        if (!requiresOnboardingSurvey && stepIndex === 0) {
            setStepIndex(1);
        }
    });

    useEffect(() => {
        onMount();
    }, [onMount]);

    useEffect(() => {
        if (!userLoading) {
            onSurveyStatusChange(userData?.requiresOnboardingSurvey);
        }
    }, [userLoading, userData?.requiresOnboardingSurvey, onSurveyStatusChange]);

    useEffect(() => {
        amplitudeService.setUserProp(AmplitudeUserProperties.IS_USER_BLOCKED_FROM_FREE, isAppBlocked);
    }, [isAppBlocked]);

    if (loading) {
        return <LoadingView isAppLoading />;
    } else {
        return stepContent;
    }
}

export default OnboardingPage;
